import { useDeleteFileMutation, useUploadFileMutation } from '@pba/react-chat-core';
import { isAxiosError } from 'axios';
import { isImageContentType } from 'models';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, message } from 'ui';
import { useGetMediaSource } from 'features/media';
import type { ChatInputAttachment, ChatInputFormValues } from '../ChatInput';
import { useGetChatInputAttachmentSelectionError } from './useGetChatInputAttachmentSelectionError';

const isCanceledRequest = (error: unknown) => isAxiosError(error) && error.code === 'ERR_CANCELED';

export const useChatInputAttachments = () => {
  const { t } = useTranslation();
  const getMediaSource = useGetMediaSource();
  const getChatInputAttachmentSelectionError = useGetChatInputAttachmentSelectionError();
  const { getFieldsValue, setFieldValue } = Form.useFormInstance<ChatInputFormValues>();
  const attachments = Form.useWatch<ChatInputAttachment[] | undefined>('attachments');
  const uploadAbortControllers = useRef(new Map<string, AbortController>());
  const { mutateAsync: deleteFile } = useDeleteFileMutation();
  const { mutateAsync: uploadFile } = useUploadFileMutation();

  const setAttachments = useCallback(
    (updater: (current: ChatInputAttachment[]) => ChatInputAttachment[]) => {
      setFieldValue('attachments', updater(getFieldsValue().attachments));
    },
    [getFieldsValue, setFieldValue],
  );

  useEffect(
    () => () => {
      for (const abortController of uploadAbortControllers.current.values()) {
        abortController.abort();
      }
      uploadAbortControllers.current.clear();
    },
    [],
  );

  const handleSelectFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }

      const errorMessage = getChatInputAttachmentSelectionError({
        currentAttachmentCount: getFieldsValue().attachments.length,
        files,
      });

      if (errorMessage) {
        message.error(errorMessage);
        return;
      }

      const pendingAttachments: ChatInputAttachment[] = files.map((file) => ({
        contentType: file.type,
        filename: file.name,
        id: crypto.randomUUID(),
        isLoading: true,
      }));

      setAttachments((current) => [...current, ...pendingAttachments]);

      void Promise.all(
        pendingAttachments.map(async (attachment, index) => {
          const file = files[index];

          if (!file) {
            return;
          }

          const abortController = new AbortController();
          uploadAbortControllers.current.set(attachment.id, abortController);

          try {
            const response = await uploadFile({ file, signal: abortController.signal });
            const fileId = response.data._id;

            setAttachments((current) =>
              current.map((item) =>
                item.id === attachment.id
                  ? {
                      ...item,
                      fileId,
                      isLoading: false,
                      url: isImageContentType(file.type) ? getMediaSource({ id: fileId }) : undefined,
                    }
                  : item,
              ),
            );
          } catch (error) {
            if (isCanceledRequest(error)) {
              return;
            }

            setAttachments((current) => current.filter((item) => item.id !== attachment.id));
            message.error(t("Something went wrong. File {{fileName}} couldn't be uploaded.", { fileName: file.name }));
          } finally {
            uploadAbortControllers.current.delete(attachment.id);
          }
        }),
      );
    },
    [getChatInputAttachmentSelectionError, getFieldsValue, getMediaSource, setAttachments, t, uploadFile],
  );

  const handleRemoveAttachment = useCallback(
    ({ id }: { id: string }) => {
      const currentAttachment = getFieldsValue().attachments.find((item) => item.id === id);

      setAttachments((current) => current.filter((item) => item.id !== id));

      if (currentAttachment?.fileId) {
        void deleteFile({ fileId: currentAttachment.fileId }).catch(() => {
          message.error("Something went wrong. The attachment couldn't be deleted.");
        });
        return;
      }

      uploadAbortControllers.current.get(id)?.abort();
      uploadAbortControllers.current.delete(id);
    },
    [deleteFile, getFieldsValue, setAttachments],
  );

  return useMemo(
    () => ({
      attachments,
      handleRemoveAttachment,
      handleSelectFiles,
    }),
    [attachments, handleRemoveAttachment, handleSelectFiles],
  );
};
